import { ReactNode, lazy, Suspense } from 'react';
import { useAuth } from './AuthContext';
import { AuthLoadingScreen } from './AuthLoadingScreen';

const SignedOut = lazy(() => import('./SignedOut').then(m => ({ default: m.SignedOut })));

/**
 * RequireAuth — only renders children when authenticated.
 * Shows a loading screen while AuthProvider is bootstrapping / initiating SSO redirect.
 * Shows signed-out page when user just logged out (mirrors Angular sso-login-stub at root URL).
 */
export function RequireAuth({ fallback = null, children }: { fallback?: ReactNode; children: ReactNode }) {
  const { authenticated, loading, error } = useAuth();

  // While auth is bootstrapping or SSO redirect is in flight, show a spinner.
  // The redirect to IdP will happen inside AuthProvider; user will be navigated away.
  if (loading) {
    return <AuthLoadingScreen />;
  }

  // Auth bootstrap finished but we're not authenticated.
  if (!authenticated) {
    // If user just logged out, show the signed-out page at root URL
    // (mirrors Angular: navigate to '' → LoginStubComponent → renders sso-login-stub)
    const isJustLoggedOut = sessionStorage.getItem('logout') === 'true';
    if (isJustLoggedOut) {
      return <Suspense fallback={<AuthLoadingScreen />}><SignedOut /></Suspense>;
    }

    if (error) {
      return (
          <AuthLoadingScreen   />
      );
    }
    if (fallback) {
      return <>{fallback}</>;
    }
    // SSO redirect in progress — keep showing the "signing in" screen
    return <AuthLoadingScreen />;
  }

  return <>{children}</>;
}

/**
 * RequireRole — only renders children when user has the given role/persona.
 */
export function RequireRole({
  role,
  fallback = null,
  children
}: {
  role: string;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { hasRole, authenticated, loading } = useAuth();
  if (loading) {
    return <AuthLoadingScreen  />;
  }
  if (!authenticated) {
    return <>{fallback ?? <AuthLoadingScreen />}</>;
  }
  if (!hasRole(role)) {
    return <>{fallback ?? <AuthLoadingScreen />}</>;
  }
  return <>{children}</>;
}
